import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import '../../assets/css/admin-custom.css';

interface AdminLayoutProps {
    children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const { user, loading } = useAuth();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileShow, setMobileShow] = useState(false);
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        return (localStorage.getItem('admin-theme') as 'light' | 'dark') || 'light';
    });

    useEffect(() => {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('admin-theme', theme);
    }, [theme]);

    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 px-4">
                <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 shadow-2xl shadow-slate-200/50 dark:shadow-black/20 border border-slate-100 dark:border-slate-800 text-center">
                    <div className="w-24 h-24 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-8">
                        <i className="fa-solid fa-lock text-4xl text-blue-600"></i>
                    </div>
                    <h2 className="text-3xl font-black text-blue-950 dark:text-white mb-4 tracking-tight">Admin Login Required</h2>
                    <p className="text-blue-950 dark:text-slate-400 mb-8 font-medium">Please login with your admin account to access the administration panel.</p>
                    <Link to="/login" className="inline-flex items-center justify-center w-full px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs rounded-2xl transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98]">
                        Go to Login
                    </Link>
                </div>
            </div>
        );
    }

    if (user.role !== 'admin' && user.role !== 'sub-admin') {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 px-4">
                <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 shadow-2xl shadow-slate-200/50 dark:shadow-black/20 border border-slate-100 dark:border-slate-800 text-center">
                    <div className="w-24 h-24 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-8">
                        <i className="fa-solid fa-shield-halved text-4xl text-red-600"></i>
                    </div>
                    <h2 className="text-3xl font-black text-blue-950 dark:text-white mb-4 tracking-tight">Access Denied</h2>
                    <p className="text-blue-950 dark:text-slate-400 mb-8 font-medium">Sorry, you don't have permission to access the admin panel. Admins only.</p>
                    <Link to="/" className="inline-flex items-center justify-center w-full px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs rounded-2xl transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98]">
                        Return to Homepage
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans flex overflow-hidden">
            <Sidebar
                collapsed={collapsed}
                mobileShow={mobileShow}
                setMobileShow={setMobileShow}
            />

            <div className="flex-1 flex flex-col min-w-0 relative h-screen">
                <Header
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    mobileShow={mobileShow}
                    setMobileShow={setMobileShow}
                    theme={theme}
                    toggleTheme={toggleTheme}
                />

                <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 lg:pl-10 lg:pr-4 lg:py-10 scroll-smooth">
                    <div className="max-w-[1600px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
                        {children}
                    </div>
                </main>
            </div>

            {/* Mobile Overlay */}
            {mobileShow && (
                <div
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[999] lg:hidden animate-in fade-in duration-300"
                    onClick={() => setMobileShow(false)}
                ></div>
            )}
        </div>
    );
};

export default AdminLayout;
